import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import {
  acceptSecondaryMemberInvite,
  getSecondaryMemberInviteDetails,
} from "../../../services/merchantService";
import { secondaryMemberInviteSetupSchema } from "../../../utils/validationSchemas";
import { clearClientStorage } from "../../../utils/sessionManager";
import { setUserData } from "../../../redux/slices/authSlice";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import toast from "../../../utils/toast";
import MerchantAccountSetupForm from "./MerchantAccountSetupForm";

const SecondaryMemberInvite = () => {
  const { token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      phone: "",
      phoneCountry: "AU",
      password: "",
      confirmPassword: "",
    },
    validationSchema: secondaryMemberInviteSetupSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await handleAcceptInvite({
        name: `${values.firstName} ${values.lastName}`.trim(),
        phone: values.phone,
        phoneCountry: values.phoneCountry,
        password: values.password,
      });
    },
  });

  useEffect(() => {
    const fetchInvite = async () => {
      if (!token) {
        setInviteError("Invitation token is missing.");
        setLoading(false);
        return;
      }

      try {
        const response = await dispatch(
          getSecondaryMemberInviteDetails(token)
        ).unwrap();

        if (response?.success === true) {
          setInviteData(response?.data || null);
        } else {
          setInviteError(response?.message || "Unable to load invitation details.");
        }
      } catch (error) {
        setInviteError(
          error?.message ||
            error?.error ||
            "This secondary member invitation is no longer valid."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dispatch]);

  const finalizeLogin = (responseData, successMessage) => {
    const authData = responseData?.data;
    const userData = authData?.user;
    const merchantProfiles = Array.isArray(userData?.merchantProfiles)
      ? userData.merchantProfiles
      : [];

    clearClientStorage();
    dispatch(setUserData(authData));

    const preferredBusiness =
      merchantProfiles.find((profile) => profile?.status === "approved") ||
      merchantProfiles[0] ||
      null;

    if (preferredBusiness) {
      dispatch(setCurrentBusiness(preferredBusiness));
    }

    toast.success(successMessage || responseData?.message || "Access granted successfully.");
    navigate("/merchant/dashboard", { replace: true });
  };

  const handleAcceptInvite = async (payload = {}) => {
    if (!token) return;

    setSubmitting(true);
    try {
      const response = await dispatch(
        acceptSecondaryMemberInvite({ token, payload })
      ).unwrap();

      if (response?.success === true) {
        finalizeLogin(response, response?.message);
        return;
      }

      toast.error(response?.message || "Unable to accept invitation.");
    } catch (error) {
      toast.error(error?.message || error?.error || "Unable to accept invitation.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderInvalidState = () => (
    <div className="w-full rounded-[14px] border border-[#FFD9D9] bg-[#FFF6F6] p-6 sm:p-7 text-center">
      <h3 className="font-poppins text-[18px] sm:text-[20px] font-[600] text-[#303030]">
        Invitation Unavailable
      </h3>
      <p className="mt-3 font-poppins text-[14px] sm:text-[16px] text-[#767676]">
        {inviteError || "This secondary member invitation is no longer valid."}
      </p>
    </div>
  );

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
  } = formik;

  const scrollToFirstError = (formErrors) => {
    const priority = ["firstName", "lastName", "phone", "password", "confirmPassword"];
    let targetField = priority.find(
      (key) => formErrors && Object.prototype.hasOwnProperty.call(formErrors, key)
    );

    if (!targetField) {
      const keys = Object.keys(formErrors || {});
      targetField = keys.length ? keys[0] : null;
    }

    if (!targetField) return;

    const el =
      document.querySelector(`[data-field="${targetField}"]`) ||
      document.querySelector(`[name="${targetField}"]`);

    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") {
        try {
          el.focus();
        } catch (_) {}
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      formik.setTouched(
        Object.keys(validationErrors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
        true
      );
      scrollToFirstError(validationErrors);
      return;
    }
    formik.handleSubmit(e);
  };
  return (
    <MerchantAccountSetupForm
      values={formik.values}
      errors={errors}
      touched={touched}
      handleChange={handleChange}
      handleBlur={handleBlur}
      handleSubmit={handleFormSubmit}
      setFieldValue={formik.setFieldValue}
      loading={loading || submitting}
      showPhoneField
      phoneFieldVariant="member-invite"
      showEmailField
      emailValue={inviteData?.email || ""}
      showPassword={showPassword}
      togglePassword={() => setShowPassword((prev) => !prev)}
      showConfirmPassword={showConfirmPassword}
      toggleConfirmPassword={() => setShowConfirmPassword((prev) => !prev)}
      submitLabel={submitting ? "Creating..." : "Create Account"}
      submitDisabled={submitting || Boolean(inviteError && !inviteData)}
      statusContent={inviteError && !inviteData ? (
        <div className="w-full mt-6 sm:mt-10">{renderInvalidState()}</div>
      ) : null}
      hideFields={Boolean(inviteError && !inviteData)}
      showSubmitButton={!inviteError || Boolean(inviteData)}
    />
  );
};

export default SecondaryMemberInvite;
